import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Trash2, Loader } from 'lucide-react';
import { chatWithCall } from '../api';

function inlineMarkdown(text) {
  // Process inline: **bold**, `code`, then "quoted text"
  const parts = [];
  let remaining = text;
  let key = 0;
  const regex = /(\*\*(.+?)\*\*|`(.+?)`|"(>.*?)")/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(remaining)) !== null) {
    if (match.index > lastIndex) parts.push(<span key={key++}>{remaining.slice(lastIndex, match.index)}</span>);
    if (match[2]) parts.push(<strong key={key++} style={{ color: '#c4b5fd' }}>{match[2]}</strong>);
    else if (match[3]) parts.push(<code key={key++} style={{ background: '#2d2d44', padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>{match[3]}</code>);
    else if (match[4]) parts.push(<span key={key++} style={{ borderLeft: '3px solid #818cf8', paddingLeft: 8, marginLeft: 4, color: '#a5b4fc', fontStyle: 'italic', display: 'inline' }}>{match[4]}</span>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < remaining.length) parts.push(<span key={key++}>{remaining.slice(lastIndex)}</span>);
  return parts.length > 0 ? parts : [<span key={0}>{text}</span>];
}

function renderMarkdown(text) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Block quote
    if (line.startsWith('>')) {
      return <div key={i} style={{ borderLeft: '3px solid #818cf8', paddingLeft: 10, marginBottom: 4, color: '#a5b4fc', fontStyle: 'italic' }}>{inlineMarkdown(line.slice(1).trim())}</div>;
    }
    // Bullet list item (* or -)
    const bulletMatch = line.match(/^(\s*)[*\-]\s+(.*)/);
    if (bulletMatch) {
      const indent = Math.floor((bulletMatch[1].length || 0) / 2);
      return <div key={i} style={{ paddingLeft: 12 + indent * 16, marginBottom: 3, display: 'flex', gap: 6 }}><span style={{ color: '#818cf8' }}>{'•'}</span><span>{inlineMarkdown(bulletMatch[2])}</span></div>;
    }
    // Empty line
    if (line.trim() === '') return <div key={i} style={{ height: 8 }} />;
    // Normal line
    return <div key={i}>{inlineMarkdown(line)}</div>;
  });
}

export default function ChatPanel({ callId, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;

    const userMsg = { role: 'user', content: q };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      // Send history (excluding the transcript context message) capped at 20
      const history = updated.map(m => ({ role: m.role, content: m.content })).slice(-20);
      const res = await chatWithCall(callId, q, history.slice(0, -1));
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Error al comunicarse con la IA';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={panel}>
        {/* Header */}
        <div style={header}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Indagar con IA</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setMessages([])} style={iconBtn} title="Limpiar conversación">
              <Trash2 size={15} />
            </button>
            <button onClick={onClose} style={iconBtn} title="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div style={messagesArea}>
          {messages.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
              Pregunta lo que quieras sobre esta llamada.<br />
              Ejemplo: "¿El agente ofreció alternativas?" o "¿Cuál fue la queja del cliente?"
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
              <div style={{
                ...bubble,
                background: m.role === 'user' ? '#818cf8' : '#1a1a2e',
                color: m.role === 'user' ? '#fff' : '#e2e8f0',
                borderTopRightRadius: m.role === 'user' ? 4 : 12,
                borderTopLeftRadius: m.role === 'user' ? 12 : 4,
              }}>
                {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
              <div style={{ ...bubble, background: '#1a1a2e', color: '#818cf8' }}>
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Pensando...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={inputArea}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe tu pregunta..."
            rows={2}
            style={inputField}
            disabled={loading}
          />
          <button onClick={handleSend} disabled={loading || !input.trim()} style={{
            ...sendBtn,
            opacity: loading || !input.trim() ? 0.4 : 1,
          }}>
            <Send size={16} />
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const overlay = {
  position: 'fixed', top: 0, right: 0, bottom: 0,
  width: 420, zIndex: 1000,
  display: 'flex', flexDirection: 'column',
};

const panel = {
  display: 'flex', flexDirection: 'column',
  height: '100%', background: '#0f0f13',
  borderLeft: '1px solid #2d2d44',
  boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
};

const header = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '14px 16px', borderBottom: '1px solid #2d2d44',
  color: '#e2e8f0',
};

const iconBtn = {
  background: 'none', border: 'none', color: '#94a3b8',
  cursor: 'pointer', padding: 4, borderRadius: 4,
};

const messagesArea = {
  flex: 1, overflowY: 'auto', padding: '16px',
};

const bubble = {
  maxWidth: '85%', padding: '10px 14px',
  borderRadius: 12, fontSize: 13, lineHeight: 1.6,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

const inputArea = {
  display: 'flex', gap: 8, padding: '12px 16px',
  borderTop: '1px solid #2d2d44', alignItems: 'flex-end',
};

const inputField = {
  flex: 1, background: '#1a1a2e', color: '#e2e8f0',
  border: '1px solid #2d2d44', borderRadius: 8,
  padding: '10px 12px', fontSize: 13, resize: 'none',
  outline: 'none', fontFamily: 'inherit',
};

const sendBtn = {
  background: '#818cf8', color: '#fff', border: 'none',
  borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
  display: 'flex', alignItems: 'center',
};
